import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
    MigrationPlan,
    MigrationStatusResponse,
} from '../../model/types/plan-migration';
import { FoundersClubBanner } from '../founders-club-banner/founders-club-banner';
import { MigrationPlanCard } from '../migration-plan-card/migration-plan-card';

import cls from './plan-selector.module.css';

type BillingPeriod = 'monthly' | 'yearly';

interface PlanSelectorProps {
    migrationData: MigrationStatusResponse;
    title?: string;
    subtitle?: string;
    isLoading?: boolean;
    selectedPriceId?: string | null;
    onSelectPlan: (priceId: string) => void;
}

export const PlanSelector = memo((props: PlanSelectorProps) => {
    const {
        migrationData,
        title,
        subtitle,
        isLoading = false,
        selectedPriceId = null,
        onSelectPlan,
    } = props;
    const { t } = useTranslation('subscription');

    const [billingPeriod, setBillingPeriod] =
        useState<BillingPeriod>('monthly');

    const handleSetMonthly = useCallback(() => {
        setBillingPeriod('monthly');
    }, []);

    const handleSetYearly = useCallback(() => {
        setBillingPeriod('yearly');
    }, []);

    const isPlanLoading = (plan: MigrationPlan) =>
        isLoading &&
        (selectedPriceId === plan.monthly_price_id ||
            selectedPriceId === plan.yearly_price_id);

    return (
        <div className={cls.root}>
            {(title || subtitle) && (
                <div className={cls.titleSection}>
                    {title && <h1 className={cls.title}>{title}</h1>}
                    {subtitle && <p className={cls.subtitle}>{subtitle}</p>}
                </div>
            )}

            {migrationData.founders_club_eligible && (
                <FoundersClubBanner
                    discountDurationMonths={
                        migrationData.discount_duration_months
                    }
                    discountPercent={migrationData.discount_percent}
                />
            )}

            <div className={cls.periodToggleWrapper}>
                <div className={cls.periodToggle}>
                    <button
                        className={`${cls.periodOption} ${billingPeriod === 'monthly' ? cls.active : ''}`}
                        onClick={handleSetMonthly}
                        type="button"
                    >
                        {t('migration.monthly')}
                    </button>
                    <div className={cls.yearlyOptionWrapper}>
                        <span className={cls.discountBadge}>
                            {t('annualDiscount')}
                        </span>
                        <button
                            className={`${cls.periodOption} ${billingPeriod === 'yearly' ? cls.active : ''}`}
                            onClick={handleSetYearly}
                            type="button"
                        >
                            {t('migration.yearly')}
                        </button>
                    </div>
                </div>
            </div>

            <div className={cls.planGrid}>
                {migrationData.available_plans.map((plan) => (
                    <div
                        key={plan.key}
                        className={cls.planColumn}
                    >
                        <MigrationPlanCard
                            billingPeriod={billingPeriod}
                            currentPlan={migrationData.current_plan}
                            discountDurationMonths={
                                migrationData.discount_duration_months
                            }
                            discountPercent={migrationData.discount_percent}
                            isFoundersEligible={
                                migrationData.founders_club_eligible
                            }
                            isLoading={isPlanLoading(plan)}
                            onSelect={onSelectPlan}
                            plan={plan}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
});

PlanSelector.displayName = 'PlanSelector';
